import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import axios from 'axios';
import { Users, BookOpen, PlayCircle, TrendingUp, Loader2, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
    const [stats, setStats] = useState({
        totalStudents: 0,
        totalCourses: 0,
        totalLessons: 0,
        totalSignals: 0,
        openSignals: 0,
    });
    const [recentStudents, setRecentStudents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [studentsRes, coursesRes, signalsRes] = await Promise.all([
                axios.get(`${API}/admin/students`, { withCredentials: true }),
                axios.get(`${API}/courses`, { withCredentials: true }),
                axios.get(`${API}/signals`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            
            const students = studentsRes.data;
            const courses = coursesRes.data;
            const signals = signalsRes.data || [];
            const totalLessons = courses.reduce((sum, c) => sum + (c.lesson_count || 0), 0);
            
            setStats({
                totalStudents: students.length,
                totalCourses: courses.length,
                totalLessons,
                totalSignals: signals.length,
                openSignals: signals.filter((s) => s.status === 'open').length,
            });
            
            // Get 5 most recent students
            setRecentStudents(students.slice(0, 5));
        } catch (e) {
            console.error('Error fetching data:', e);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getPhaseColor = (phase) => {
        const colors = {
            safe_start: '#3B82F6',
            building_consistency: '#8B5CF6',
            capital_confidence: '#D4AF37'
        };
        return colors[phase] || '#94A3B8';
    };

    const formatPhaseName = (phase) => {
        if (!phase) return 'N/A';
        return phase.replace(/_/g, ' ').toUpperCase();
    };

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="admin-dashboard-title">
                        Admin Dashboard
                    </h1>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Manage your academy and track student progress
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
                    <div className="card-surface p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                <Users className="w-6 h-6" style={{ color: '#3B82F6' }} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{stats.totalStudents}</p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Students</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-surface p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                                <BookOpen className="w-6 h-6" style={{ color: '#8B5CF6' }} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{stats.totalCourses}</p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Courses</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-surface p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--primary-muted)', border: '1px solid var(--border-highlight)' }}>
                                <PlayCircle className="w-6 h-6" style={{ color: 'var(--primary-color)' }} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{stats.totalLessons}</p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Lessons</p>
                            </div>
                        </div>
                    </div>

                    <a href="/admin/signals" className="card-surface p-6 hover:border-[var(--border-highlight)] transition-all" data-testid="stats-signals-card">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                                <Radio className="w-6 h-6" style={{ color: '#10B981' }} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{stats.totalSignals}</p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Signals ({stats.openSignals} open)</p>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Recent Students */}
                <div className="card-surface p-6" data-testid="recent-students-section">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                            Recent Students
                        </h2>
                        <a href="/admin/students" className="text-sm font-medium hover:underline" style={{ color: 'var(--primary-color)' }}>
                            View All
                        </a>
                    </div>
                    
                    {recentStudents.length === 0 ? (
                        <div className="text-center py-8">
                            <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No students enrolled yet</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header">
                                        <th className="text-left py-3 px-4">Name</th>
                                        <th className="text-left py-3 px-4">Email</th>
                                        <th className="text-left py-3 px-4">Phase</th>
                                        <th className="text-left py-3 px-4">Enrolled</th>
                                        <th className="text-center py-3 px-4">Lessons</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentStudents.map((student) => (
                                        <tr key={student.id} className="table-row">
                                            <td className="py-4 px-4">
                                                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{student.name}</span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span style={{ color: 'var(--text-secondary)' }}>{student.email}</span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span 
                                                    className="px-2 py-1 rounded text-xs font-semibold"
                                                    style={{ 
                                                        backgroundColor: `${getPhaseColor(student.current_phase)}15`,
                                                        color: getPhaseColor(student.current_phase),
                                                        border: `1px solid ${getPhaseColor(student.current_phase)}30`
                                                    }}
                                                >
                                                    {formatPhaseName(student.current_phase)}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span style={{ color: 'var(--text-muted)' }}>{formatDate(student.enrolled_at)}</span>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <span style={{ color: 'var(--text-secondary)' }}>{student.lessons_completed || 0}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
